import type { EditableRow, NetworkFeeType, SupportedMethod } from './providerFeesTypes';

export const normalizeSupportedMethodId = (m: SupportedMethod): string => {
  const raw = (m.methodId || m.id || '').trim();
  return raw;
};

export const formatProviderDisplayName = (providerRaw: string): string => {
  const provider = (providerRaw || '').trim().toLowerCase();
  if (provider === 'zeroxprocessing_static_wallet') return '0xProcessing Static Wallet';
  if (provider === 'online_debt') return 'Internal transaction fees';
  if (provider === 'zeroxprocessing') return '0xProcessing';
  if (provider === 'linkmepay') return 'LinkMePay';
  if (provider === 'ggusonepay') return 'GGUSOnePay';
  if (provider === 'btcpay') return 'BTCPay';
  if (provider === 'meld') return 'Meld';
  return providerRaw || provider;
};

export const getProviderGroupKey = (row: Pick<EditableRow, 'provider' | 'methodId'>): string => {
  const provider = (row.provider || '').trim().toLowerCase();
  const methodId = (row.methodId || '').trim().toLowerCase();
  if (provider === 'zeroxprocessing' && methodId === 'zeroxprocessing_static_wallet') {
    return 'zeroxprocessing_static_wallet';
  }
  return provider || 'unknown';
};

export const formatOnlineDebtMethodLabel = (methodIdRaw: string): string => {
  const methodId = String(methodIdRaw || '').trim().toLowerCase();
  if (methodId === 'internal' || methodId === 'all') return 'Internal USD Wallet Transaction';
  return 'Internal USD Wallet Transaction';
};

const BTCPAY_WITHDRAWAL_METHOD_LABELS: Record<string, { name: string; ticker: string }> = {
  bitcoin_transfer: { name: 'Bitcoin On-Chain', ticker: 'BTC' },
  litecoin_transfer: { name: 'Litecoin', ticker: 'LTC' },
  dogecoin_transfer: { name: 'Dogecoin', ticker: 'DOGE' },
};

export const formatBtcpayWithdrawalMethodLabel = (methodIdRaw: string): string => {
  const methodId = String(methodIdRaw || '').trim().toLowerCase();
  const mapped = BTCPAY_WITHDRAWAL_METHOD_LABELS[methodId];
  if (mapped) return mapped.name;
  return methodIdRaw || 'Method';
};

const HARD_CODED_SUPPORTED_METHODS_BY_KEY: Record<string, SupportedMethod[]> = {
  'linkmepay::deposit': [
    { methodId: 'ecash_app', name: 'ECash App', ticker: 'ECASH' },
    { methodId: 'paypal', name: 'PayPal', ticker: 'PAYPAL' },
  ],
  'ggusonepay::deposit': [
    { methodId: 'cashapp', name: 'Cash App', ticker: 'CASHAPP' },
    { methodId: 'chime', name: 'Chime', ticker: 'CHIME' },
    { methodId: 'applepay', name: 'Apple Pay', ticker: 'APPLEPAY' },
    { methodId: 'googlepay', name: 'Google Pay', ticker: 'GOOGLEPAY' },
    { methodId: 'ggus_btcpay', name: 'BTC Lightning (Cash App)', ticker: 'BTC' },
  ],
  'ggusonepay::withdrawal': [
    { methodId: 'cashapp_transfer', name: 'Cash App', ticker: 'CASHAPP' },
    { methodId: 'chime_transfer', name: 'Chime', ticker: 'CHIME' },
  ],
  'btcpay::deposit': [
    { methodId: 'btc_onchain', name: 'Bitcoin On-Chain', ticker: 'BTC' },
    { methodId: 'ltc_chain', name: 'Litecoin', ticker: 'LTC' },
    { methodId: 'doge_chain', name: 'Dogecoin', ticker: 'DOGE' },
  ],
  'btcpay::withdrawal': [
    { methodId: 'bitcoin_transfer', name: 'Bitcoin On-Chain', ticker: 'BTC' },
    { methodId: 'litecoin_transfer', name: 'Litecoin', ticker: 'LTC' },
    { methodId: 'dogecoin_transfer', name: 'Dogecoin', ticker: 'DOGE' },
  ],
};

export const mergeWithHardcodedSupportedMethods = (
  providerRaw: string,
  methods: SupportedMethod[],
  operationRaw = ''
): SupportedMethod[] => {
  const provider = (providerRaw || '').trim().toLowerCase();
  const operation = String(operationRaw || '').trim().toLowerCase();
  const hardcoded =
    HARD_CODED_SUPPORTED_METHODS_BY_KEY[`${provider}::${operation}`] ||
    HARD_CODED_SUPPORTED_METHODS_BY_KEY[`${provider}::`] ||
    [];

  const out: SupportedMethod[] = [];
  const seen = new Set<string>();

  const push = (m: SupportedMethod) => {
    const id = normalizeSupportedMethodId(m).trim().toLowerCase();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(m);
  };

  (Array.isArray(methods) ? methods : []).forEach(push);
  hardcoded.forEach(push);

  return out;
};

/** Parse zerox_* method_id when catalog name is unavailable. */
export const formatZeroxMethodIdLabel = (methodIdRaw: string): string => {
  const id = String(methodIdRaw || '').trim().toLowerCase();
  if (!id.startsWith('zerox_')) return methodIdRaw || 'Method';
  const rest = id.replace(/^zerox_/, '');
  if (rest === 'eth') return 'ETH';
  if (rest === 'usdt') return 'USDT';
  if (rest.startsWith('eth_')) {
    const network = rest
      .slice(4)
      .split('_')
      .filter(Boolean)
      .map((part) => part.toUpperCase())
      .join(' ');
    return network ? `ETH (${network})` : 'ETH';
  }
  if (rest.startsWith('usdt_')) {
    const network = rest
      .slice(5)
      .split('_')
      .filter(Boolean)
      .map((part) => part.toUpperCase())
      .join(' ');
    return network ? `USDT (${network})` : 'USDT';
  }
  return rest.toUpperCase();
};

export const prettyMethodLabel = (m: SupportedMethod): string => {
  const methodId = normalizeSupportedMethodId(m);
  const displayName = String(m.name || m.currency || '').trim();
  const ticker = String(m.ticker || '').trim();

  // 0x variants include network in currency label, e.g. "ETH (BASE)" / "USDT (POLYGON)"
  if (displayName.includes('(')) return displayName;
  if (displayName && ticker && displayName.length > ticker.length) return displayName;

  const t = (ticker || displayName).toUpperCase();
  const inferredNetwork =
    t === 'BTC'
      ? 'Bitcoin Network'
      : t === 'LTC'
        ? 'Litecoin Network'
        : t === 'DOGE'
          ? 'Dogecoin Network'
          : t
            ? `${t} Network`
            : '';
  if (ticker && inferredNetwork) return `${ticker} — ${inferredNetwork}`;
  if (displayName) return displayName;
  if (ticker) return ticker;
  if (methodId.startsWith('zerox_')) return formatZeroxMethodIdLabel(methodId);
  if (BTCPAY_WITHDRAWAL_METHOD_LABELS[methodId]) return formatBtcpayWithdrawalMethodLabel(methodId);
  return methodId || 'Method';
};

export const splitCoinAndNetworkLabel = (
  methodLabel: string,
  methodId = ''
): { coin: string; network: string } => {
  const label = String(methodLabel || '').trim();
  const id = String(methodId || '').trim().toLowerCase();

  const parenMatch = label.match(/^([^(]+)\(([^)]+)\)\s*$/);
  if (parenMatch) {
    return {
      coin: parenMatch[1].trim(),
      network: parenMatch[2].trim(),
    };
  }

  const split = label.split(/\s*[-—–/|]\s*|\s{2,}/);
  if (split.length >= 2 && split[1]?.trim()) {
    return { coin: split[0].trim(), network: split[1].trim() };
  }

  if (id.startsWith('zerox_eth_')) {
    return {
      coin: 'ETH',
      network: id
        .replace('zerox_eth_', '')
        .split('_')
        .filter(Boolean)
        .map((p) => p.toUpperCase())
        .join(' '),
    };
  }
  if (id.startsWith('zerox_usdt_')) {
    return {
      coin: 'USDT',
      network: id
        .replace('zerox_usdt_', '')
        .split('_')
        .filter(Boolean)
        .map((p) => p.toUpperCase())
        .join(' '),
    };
  }
  if (id === 'zerox_eth') return { coin: 'ETH', network: 'Default' };
  if (id === 'zerox_usdt') return { coin: 'USDT', network: 'Default' };

  return { coin: label || '—', network: '' };
};

/** feeRate is decimal (e.g. 0.06 = 6%). Rounds display percent to 2 dp to avoid float noise. */
export const toPercentString = (feeRate: number): string => {
  const n = Number(feeRate);
  if (!Number.isFinite(n) || n < 0) return '0';
  const pct = n * 100;
  const rounded = Math.round(pct * 100 + Number.EPSILON) / 100;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(rounded);
};

export const toRateNumber = (feePercent: string): number => {
  const p = Number(String(feePercent || '').trim());
  if (!Number.isFinite(p) || p < 0) return 0;
  return p / 100;
};

export const toMoneyString = (amount: number): string => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

export const normalizeNetworkFeeType = (raw: unknown): NetworkFeeType => {
  const t = String(raw || 'none').trim().toLowerCase();
  if (t === 'percent' || t === 'fixed') return t;
  return 'none';
};

export const formatNetworkFeeDisplay = (
  type: NetworkFeeType,
  percent: string,
  fixedUsd: string
): string => {
  if (type === 'percent') return `${percent || '0'}%`;
  if (type === 'fixed') return `$${fixedUsd || '0'}`;
  return '—';
};

export const networkFeeFieldsFromItem = (it: {
  networkFeeType?: string;
  networkFeeRate?: number;
  networkFeeAmount?: number;
}) => {
  const networkFeeType = normalizeNetworkFeeType(it.networkFeeType);
  return {
    networkFeeType,
    networkFeePercent: networkFeeType === 'percent' ? toPercentString(it.networkFeeRate ?? 0) : '0',
    networkFeeFixedUsd:
      networkFeeType === 'fixed' ? toMoneyString(Number(it.networkFeeAmount ?? 0)) : '0',
  };
};

export const networkFeePayloadFromRow = (row: Pick<
  EditableRow,
  'networkFeeType' | 'networkFeePercent' | 'networkFeeFixedUsd'
>) => {
  const networkFeeType = normalizeNetworkFeeType(row.networkFeeType);
  if (networkFeeType === 'percent') {
    return {
      networkFeeType,
      networkFeeRate: toRateNumber(row.networkFeePercent),
      networkFeeAmount: 0,
    };
  }
  if (networkFeeType === 'fixed') {
    const fixed = Number(String(row.networkFeeFixedUsd || '').trim());
    return {
      networkFeeType,
      networkFeeRate: 0,
      networkFeeAmount: Number.isFinite(fixed) && fixed >= 0 ? fixed : 0,
    };
  }
  return { networkFeeType: 'none' as const, networkFeeRate: 0, networkFeeAmount: 0 };
};

export const toSupportedKey = (provider: string, operation: string) =>
  `${(provider || '').trim().toLowerCase()}::${String(operation || '').trim().toLowerCase()}`;

const PROVIDER_DISPLAY_ORDER = [
  'online_debt',
  'btcpay',
  'linkmepay',
  'ggusonepay',
  'meld',
  'zeroxprocessing_static_wallet',
  'zeroxprocessing',
] as const;

export const providerSortRank = (providerRaw: string): number => {
  const provider = (providerRaw || '').trim().toLowerCase();
  const idx = PROVIDER_DISPLAY_ORDER.indexOf(provider as (typeof PROVIDER_DISPLAY_ORDER)[number]);
  return idx >= 0 ? idx : PROVIDER_DISPLAY_ORDER.length;
};

export const methodSortLabel = (
  row: Pick<EditableRow, 'provider' | 'operation' | 'methodId'>,
  supportedByKey: Record<string, SupportedMethod[]>
): string => {
  const provider = (row.provider || '').trim().toLowerCase();
  const operation = String(row.operation || '').trim().toLowerCase();
  const methodId = (row.methodId || 'all').trim().toLowerCase();
  if (provider === 'online_debt') return formatOnlineDebtMethodLabel(methodId);
  const supported = supportedByKey[toSupportedKey(provider, operation)] || [];
  const match = supported.find((m) => normalizeSupportedMethodId(m).toLowerCase() === methodId);
  if (match) return prettyMethodLabel(match);
  if (methodId.startsWith('zerox_')) return formatZeroxMethodIdLabel(methodId);
  return methodId;
};

/** Stable list order: provider group, operation, then method display name. */
export const sortProviderFeeRows = (
  rows: EditableRow[],
  supportedByKey: Record<string, SupportedMethod[]>
): EditableRow[] => {
  return [...rows].sort((a, b) => {
    const providerRank = providerSortRank(a.provider) - providerSortRank(b.provider);
    if (providerRank !== 0) return providerRank;

    const providerCmp = (a.provider || '').localeCompare(b.provider || '', undefined, { sensitivity: 'base' });
    if (providerCmp !== 0) return providerCmp;

    const operationCmp = String(a.operation || '').localeCompare(String(b.operation || ''));
    if (operationCmp !== 0) return operationCmp;

    const methodCmp = methodSortLabel(a, supportedByKey).localeCompare(
      methodSortLabel(b, supportedByKey),
      undefined,
      { sensitivity: 'base', numeric: true }
    );
    if (methodCmp !== 0) return methodCmp;

    return (a.methodId || '').localeCompare(b.methodId || '');
  });
};

